// src/lib/actingOrg.ts
//
// «Empresa en la que estoy trabajando» (ADR-007 · Contrato F1). Estado ÚNICO del
// frontend para el acceso delegado: cuando una consultora elige una empresa, las
// llamadas de los módulos que lo adoptan (hoy: Diagnósticos) agregan
// `X-Acting-Org: <cliente_org>`. El backend lo acepta SOLO con un engagement
// activo, vigente y con el scope pedido (si no → 403).
//
// Reglas (anti-leak, ver memoria «multi-tenant nunca default fallback»):
//  - NUNCA hay empresa por defecto: sin elección explícita → sin header → mi org.
//  - La elección se recuerda por SESIÓN (sessionStorage) y queda atada a la org
//    y usuario del JWT: otra sesión/otra cuenta no la hereda.
//  - Si el engagement falla o se revoca → se limpia, se avisa y se vuelve a mi org.
//
// Es un store sin React (los clientes API lo leen al armar headers) con un hook
// para la UI. Otros módulos lo adoptan con `actingOrgHeaders()` + <ActingOrgBar/>.

import { useSyncExternalStore } from 'react';
import { decodeJwt } from '@/lib/api_superadmin';
import { engagementBlocker, engagementUsable, listEngagements, type Engagement } from '@/lib/engagements';

export const ACTING_ORG_HEADER = 'X-Acting-Org';
const KEY = 'orion:acting-org';

export interface ActingOrg {
  /** org de la empresa cliente (lo que viaja en X-Acting-Org) */
  org_id: string;
  nombre: string;
  engagement_id: string;
  modo: Engagement['modo'];
  scopes: string[];
  consultora_nombre: string;
  /** org y usuario del JWT con que se eligió (si cambian, la elección no aplica) */
  principal_org: string;
  principal_user: string;
}

export interface ActingOrgState {
  acting: ActingOrg | null;
  /** Aviso cuando se volvió a mi org sin que la persona lo pidiera. */
  notice: string;
}

function principal(): { org: string; user: string } {
  const t = localStorage.getItem('auth_token');
  const p: any = t ? decodeJwt(t) : null;
  return {
    org: typeof p?.org_id === 'string' ? p.org_id : '',
    user: typeof p?.user_id === 'string' ? p.user_id : typeof p?.sub === 'string' ? p.sub : '',
  };
}

function read(): ActingOrg | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as ActingOrg;
    const me = principal();
    // Atada a la sesión que la eligió. Sin JWT o con otro → no aplica (y se borra).
    if (!a?.org_id || !me.org || a.principal_org !== me.org || a.principal_user !== me.user || a.org_id === me.org) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return a;
  } catch {
    return null;
  }
}

let state: ActingOrgState = { acting: null, notice: '' };
let hydrated = false;
const listeners = new Set<() => void>();

function ensure() {
  if (!hydrated) {
    hydrated = true;
    state = { acting: read(), notice: '' };
  }
}

function emit(next: ActingOrgState) {
  state = next;
  listeners.forEach((l) => l());
}

export function getActingOrg(): ActingOrg | null {
  ensure();
  // Re-verifica contra el JWT actual en cada lectura (cambio de cuenta en otra pestaña, logout…).
  if (state.acting && !read()) emit({ acting: null, notice: '' });
  return state.acting;
}

/** Headers a agregar a una llamada de un módulo que trabaja «dentro» de la empresa elegida. */
export function actingOrgHeaders(): Record<string, string> {
  const a = getActingOrg();
  return a ? { [ACTING_ORG_HEADER]: a.org_id } : {};
}

/** Elegir una empresa (solo engagements usables para `scope`). */
export function setActingOrg(e: Engagement, scope: string): void {
  ensure();
  const me = principal();
  if (!me.org) throw new Error('Sin sesión: no se puede elegir empresa.');
  if (e.cliente_org === me.org) {
    clearActingOrg();
    return;
  }
  if (!engagementUsable(e, scope)) throw new Error(`No puedes entrar a ${e.cliente_nombre}: ${engagementBlocker(e, scope)}.`);
  const a: ActingOrg = {
    org_id: e.cliente_org,
    nombre: e.cliente_nombre,
    engagement_id: e.id,
    modo: e.modo,
    scopes: e.scopes,
    consultora_nombre: e.consultora_nombre,
    principal_org: me.org,
    principal_user: me.user,
  };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(a));
  } catch {
    /* modo privado: vive solo en memoria */
  }
  emit({ acting: a, notice: '' });
}

/** Volver a mi org. `notice` explica por qué si no fue a petición de la persona. */
export function clearActingOrg(notice = ''): void {
  ensure();
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
  emit({ acting: null, notice });
}

export function dismissActingNotice(): void {
  ensure();
  if (state.notice) emit({ ...state, notice: '' });
}

/**
 * Re-valida la empresa elegida contra mis engagements (al abrir el módulo o tras un
 * 403 con X-Acting-Org). Si ya no está activo/vigente/con scope → vuelve a mi org
 * con aviso claro. Devuelve los engagements (para no pedirlos dos veces).
 * Si NO se pueden consultar (red/servidor), por seguridad también vuelve a mi org.
 */
export async function revalidateActingOrg(scope: string, list?: Engagement[]): Promise<Engagement[] | null> {
  const a = getActingOrg();
  let engagements: Engagement[] | null = list ?? null;
  if (!engagements) {
    try {
      engagements = await listEngagements();
    } catch {
      engagements = null;
    }
  }
  if (!a) return engagements;
  if (!engagements) {
    clearActingOrg(`No pudimos confirmar tu acceso a ${a.nombre}. Por seguridad volviste a tu organización.`);
    return null;
  }
  const e = engagements.find((x) => x.id === a.engagement_id) ?? engagements.find((x) => x.cliente_org === a.org_id);
  if (!e) {
    clearActingOrg(`Tu acceso a ${a.nombre} ya no existe. Volviste a tu organización.`);
  } else if (!engagementUsable(e, scope)) {
    clearActingOrg(`No puedes seguir trabajando en ${a.nombre}: ${engagementBlocker(e, scope)}. Volviste a tu organización.`);
  } else if (e.cliente_nombre !== a.nombre || e.modo !== a.modo || e.scopes.join() !== a.scopes.join()) {
    setActingOrg(e, scope); // refresca nombre/modo/scopes
  }
  return engagements;
}

function subscribe(l: () => void) {
  listeners.add(l);
  const onStorage = (ev: StorageEvent) => {
    if (ev.key === 'auth_token' || ev.key === null) getActingOrg();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
}

function snapshot(): ActingOrgState {
  ensure();
  return state;
}

export function useActingOrg(): ActingOrgState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
